import { useEffect, useState } from "react";
import { gaEvent, trackPurchase } from "../lib/ga";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Download, Share2, Loader2, AlertCircle, Trophy, ShieldCheck,
  RefreshCw, Sparkles, Coins, CheckCircle2, CalendarClock, X as XIcon,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import VerifiedReadinessUpsell from "../components/founder/VerifiedReadinessUpsell";
import ShareReportModal from "../components/founder/ShareReportModal";
import ShareScoreModal from "../components/ShareScoreModal";
import WebReportSections, { FIELD_LABEL } from "../components/founder/WebReportSections";
import ScoreHistorySparkline from "../components/founder/ScoreHistorySparkline";
import {
  BuyCreditsModal, PurchaseCelebrationModal,
} from "../components/founder/ReadinessReportCard";
import BookExpertModal from "../components/founder/BookExpertModal";
import { MissingCapitalDocsPanel } from "../components/founder/CapitalDocChecklist";
import { GetHelpLink } from "../components/founder/SupportTicketModal";
import UpdateDocsRerunBanner from "../components/founder/UpdateDocsRerunBanner";

const NAV = [
  { to: "/onboarding", label: "Onboarding" },
  { to: "/founder/dashboard", label: "Dashboard" },
];

const READINESS_TARGET = 70; // Market Ready

/** Authenticated founder's detailed Capital Readiness Report — web parity with the PDF. */
export default function DetailedReport() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareScoreOpen, setShareScoreOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  // Post-Paystack purchase confirmation. {creditsBalance} | null.
  const [purchaseCelebration, setPurchaseCelebration] = useState(null);
  // Post-download deep-dive prompt: banner appears after the founder
  // downloads their report PDF; the button opens the expert-session modal.
  const [deepDiveBanner, setDeepDiveBanner] = useState(false);
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [capitalPurposes, setCapitalPurposes] = useState([]);
  // Set<string> of doc_types the founder has uploaded — powers the "X of Y
  // unlocked" progress chip + step strike-through inside the Capital Unlock
  // modal. Loaded once on mount, independent of the report fetch.
  const [uploadedDocTypes, setUploadedDocTypes] = useState(new Set());

  // Legacy deep-link support: older links (e.g. bookmarks, emails) point at
  // /founder/readiness-report?rerun=1. Re-run has been consolidated on the
  // onboarding page, so bounce them there.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("rerun") !== "1") return;
    nav("/onboarding?rerun=1", { replace: true });
  }, [searchParams, nav]);

  // Initial load + post-payment poll (when redirected here from Paystack).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get("reference") || params.get("trxref");
      let purchasePaid = false;
      if (sid) {
        try {
          const { data: pollData } = await api.post(`/founder/credits/poll/${sid}`);
          if (pollData?.payment_status === "paid") {
            purchasePaid = true;
            trackPurchase(pollData, { report: "Capital Readiness Report" });
          }
        } catch (e) {
          if (process.env.NODE_ENV !== "production") console.debug("[CCR] credits poll failed:", e?.message);
        }
        window.history.replaceState({}, "", window.location.pathname);
      }
      try {
        let { data } = await api.get("/founder/readiness/report");
        // Auto-heal for founders whose Paystack payment succeeded but the
        // post-redirect poll never fired (mobile tab drop, network hiccup,
        // closed tab before redirect completed). Reconcile is idempotent
        // and cheap — one call on load if we're still locked.
        if (data && data.unlocked === false) {
          try {
            const { data: rec } = await api.post("/founder/credits/reconcile");
            if (rec?.unlocked) {
              // Re-fetch canonical report state; nudge header credit chip.
              const refreshed = await api.get("/founder/readiness/report");
              data = refreshed.data;
              try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
              if (!purchasePaid && rec.fulfilled_now > 0) {
                // Also celebrate a recovered payment even if the URL didn't
                // carry the Paystack reference this time round.
                purchasePaid = true;
              }
            }
          } catch (e) {
            if (process.env.NODE_ENV !== "production") console.debug("[CCR] auto-reconcile skipped:", e?.message);
          }
        }
        if (!cancelled) {
          setReport(data);
          if (purchasePaid) {
            // Nudge AppShell header chip to re-fetch immediately.
            try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
            setPurchaseCelebration({ creditsBalance: data?.credits_balance ?? 0 });
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "Couldn't load report");
          // Still surface the purchase confirmation even if the report fetch
          // hiccups — otherwise the founder lands on an error screen with no
          // sign their payment went through.
          if (purchasePaid) {
            try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
            setPurchaseCelebration({ creditsBalance: 0 });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Fetch uploaded docs in parallel — never blocks the main render path.
      try {
        const { data: docs } = await api.get("/documents/me");
        if (!cancelled) {
          setUploadedDocTypes(new Set((docs || []).map((d) => d.doc_type).filter(Boolean)));
        }
        const { data: comp } = await api.get("/companies/me");
        if (!cancelled && Array.isArray(comp?.capital_purposes)) {
          setCapitalPurposes(comp.capital_purposes);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("[CCR] docs fetch failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hash-scroll: when a founder lands here from a "How to lift this score"
  // tooltip on the dashboard (e.g. `#pillar-row-P1` or `#score-breakdown`),
  // scroll the matching anchor into view as soon as the report data has
  // rendered. We pulse a row briefly so the founder's eye locks onto it.
  useEffect(() => {
    if (!report) return;
    const hash = (window.location.hash || "").replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (hash.startsWith("pillar-row-")) {
        el.classList.add("ring-2", "ring-[#00828F]", "ring-offset-2", "bg-[#00828F]/5");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-[#00828F]", "ring-offset-2", "bg-[#00828F]/5");
        }, 2400);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [report]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/founder/readiness/report");
      setReport(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[CCR] refresh failed:", e?.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/founder/readiness/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clinq-capital-readiness-report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      gaEvent("report_download", { report: "capital_readiness" });
      setDeepDiveBanner(true);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't download report.");
    }
  };

  // Render the purchase celebration overlay independently of the report
  // state. After a Paystack redirect the report may briefly be loading or
  // even error out (e.g. backend hiccup) — but the founder still needs to
  // see their payment was confirmed. We render the modal on every branch.
  const celebrationModal = purchaseCelebration ? (
    <PurchaseCelebrationModal
      creditsBalance={purchaseCelebration.creditsBalance}
      onClose={() => setPurchaseCelebration(null)}
      onRunRerun={() => {
        setPurchaseCelebration(null);
        if (report) nav("/onboarding?rerun=1");
      }}
      canRunRerun={!!report}
    />
  ) : null;

  if (loading) {
    return <AppShell nav={NAV}>
      {celebrationModal}
      <div className="mx-auto flex max-w-3xl items-center justify-center px-4 py-24 text-sm text-neutral-500"
           data-testid="detailed-report-loading">
        <Loader2 className="mr-2 h-4 w-4 animate-spin"/> Loading your report…
      </div>
    </AppShell>;
  }

  if (err || !report) {
    return <AppShell nav={NAV}>
      {celebrationModal}
      <div className="mx-auto max-w-3xl px-4 py-12" data-testid="detailed-report-error">
        <Link to="/onboarding" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#00828F]">
          <ArrowLeft className="h-4 w-4"/> Back to onboarding
        </Link>
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-600"/>
          <p className="mt-3 text-sm text-amber-900">{err || "Report not available yet."}</p>
        </div>
      </div>
    </AppShell>;
  }

  const verifiedStatus = report?.verified_readiness?.status || "none";
  const isVerified = verifiedStatus === "verified" && report?.verified_readiness?.verified_score != null;
  const displayScore = isVerified ? report.verified_readiness.verified_score : (report.readiness_score || 0);
  const reportTitle = isVerified ? "Verified Capital Readiness Report" : "Indicative Capital Readiness Report";
  // Edits the founder has made since their last analysis. When >0 we surface
  // a clickable nudge inside the score header so they re-run before the
  // stale number leaves their funder shortlist. The list of changed fields
  // is surfaced inline so the founder can decide whether the edit is
  // material enough to spend a credit on.
  const edits = Number(report?.company?.edits_since_last_analysis || 0);
  const editedFields = Array.isArray(report?.company?.edited_fields_since_last_analysis)
    ? report.company.edited_fields_since_last_analysis
    : [];
  const editedFieldLabels = editedFields.map((k) => FIELD_LABEL[k] || k);
  const rerunLocked = verifiedStatus === "pending" || verifiedStatus === "verified";
  const noCredits = (report.credits_balance || 0) === 0;
  const showRerunNudge = edits > 0 && !rerunLocked;
  const onRerunNudgeClick = () => {
    if (noCredits) setBuyOpen(true);
    else nav("/onboarding?rerun=1");
  };

  return (
    <AppShell nav={NAV}>
      {deepDiveBanner && (
        <div data-testid="deep-dive-banner"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[#00828F]/30 bg-[#00303F] px-4 py-4 shadow-2xl">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-[#4FB0BB]" />
              <div>
                <p className="text-sm font-bold text-white">Book your free deep-dive session with your Relationship Manager</p>
                <p className="mt-0.5 text-xs text-white/70">
                  Walk through your report together — which funders fit, whether you&apos;re ready, and what to strengthen first.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setDeepDiveOpen(true)}
                data-testid="deep-dive-book-btn"
                className="rounded-full bg-[#0A8B85] px-5 py-2 text-xs font-bold text-white hover:bg-[#066667]">
                Book my free session
              </button>
              <button onClick={() => setDeepDiveBanner(false)} aria-label="Dismiss"
                data-testid="deep-dive-dismiss"
                className="rounded-md p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <BookExpertModal open={deepDiveOpen} onClose={() => setDeepDiveOpen(false)}
        onBooked={() => setDeepDiveBanner(false)} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
        <Link to="/onboarding" data-testid="detailed-report-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#00828F]">
          <ArrowLeft className="h-4 w-4"/> Back to onboarding
        </Link>

        {/* Upload updated documents + re-run, without leaving the report */}
        <UpdateDocsRerunBanner report={report} locked={rerunLocked} onBuy={() => setBuyOpen(true)}
          onRerunDone={async () => {
            try {
              const { data } = await api.get("/founder/readiness/report");
              setReport(data);
              window.dispatchEvent(new Event("clinq:credits-changed"));
              window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (_) { window.location.reload(); }
          }} />

        {/* Score header — branded card */}
        <section data-testid="detailed-report-header"
                 className="mt-5 overflow-hidden rounded-2xl bg-gradient-to-br from-[#00303F] via-[#0A4543] to-[#00828F] p-6 text-white sm:p-8">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4FB0BB]">
            <ShieldCheck className="h-3.5 w-3.5"/> {reportTitle}
          </p>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl"
              data-testid="detailed-report-title">
            Your Capital Readiness Report
          </h1>
          <div className="mt-5 flex flex-wrap items-end gap-x-6 gap-y-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">
                {isVerified ? "Verified CCR Score" : "Indicative CCR Score"}
              </p>
              <p className="text-6xl font-extrabold leading-none text-white"
                 data-testid="detailed-report-score">
                {displayScore}<span className="text-2xl text-white/40"> / 100</span>
              </p>
            </div>
            {isVerified && (
              <span data-testid="detailed-report-verified-seal"
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/25 px-3 py-1 text-xs font-bold text-emerald-200">
                <ShieldCheck className="h-3.5 w-3.5"/> Verified by Clinq
              </span>
            )}
            {!isVerified && displayScore >= READINESS_TARGET && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-bold text-emerald-300">
                <Trophy className="h-3.5 w-3.5"/> Market Ready threshold reached
              </span>
            )}
            {/* Score-history sparkline — only renders when ≥2 historical
                runs exist. Lets the founder see their progress at a glance. */}
            <ScoreHistorySparkline
              history={report.score_history}
              currentScore={displayScore}
            />
          </div>

          {/* Stale-score nudge: founder has edited snapshot fields since the
              last analysis. The current score may no longer reflect their
              corrected data — click-through opens the RerunModal (or
              BuyCreditsModal when out of credits). The first few field
              labels are surfaced inline so the founder can judge whether
              the edit is material enough to spend a credit on. */}
          {showRerunNudge && (
            <button
              type="button"
              data-testid="detailed-report-rerun-nudge"
              onClick={onRerunNudgeClick}
              className="mt-5 inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-300/60 bg-amber-400/15 px-3 py-2 text-left text-xs font-semibold text-amber-100 transition hover:bg-amber-400/25"
              title={editedFieldLabels.length
                ? `Changed since last analysis: ${editedFieldLabels.join(", ")}`
                : "Your snapshot was edited after this score was calculated"}
            >
              <span className="inline-flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5"/>
                {edits} edit{edits === 1 ? "" : "s"} since last analysis
              </span>
              {editedFieldLabels.length > 0 && (
                <span
                  data-testid="detailed-report-rerun-nudge-fields"
                  className="font-normal normal-case tracking-normal text-amber-100/85"
                >
                  · {editedFieldLabels.slice(0, 3).join(", ")}
                  {editedFieldLabels.length > 3
                    ? ` +${editedFieldLabels.length - 3} more`
                    : ""}
                </span>
              )}
              <span className="text-amber-200/80">·</span>
              <span className="inline-flex items-center gap-1 font-bold text-amber-50">
                {noCredits ? "Add a credit to re-run" : "Re-run to refresh score"}
                <ArrowRight className="h-3.5 w-3.5"/>
              </span>
            </button>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => setShareScoreOpen(true)}
                    data-testid="detailed-report-share-score-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-neutral-900 hover:bg-amber-300">
              <Sparkles className="h-4 w-4"/> Share my score
            </button>
            <button onClick={() => setShareOpen(true)}
                    data-testid="detailed-report-share-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/20">
              <Share2 className="h-4 w-4"/> Share report
            </button>
            <button onClick={downloadPdf}
                    data-testid="detailed-report-download-pdf"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/20">
              <Download className="h-4 w-4"/> Download PDF
            </button>
          </div>
        </section>

        {/* Verified upsell / pending / badge */}
        <div className="mt-6">
          <VerifiedReadinessUpsell
            verified={report.verified_readiness}
            indicativeScore={report.readiness_score || 0}
          />
        </div>

        {/* Unreadable-document prompts (figure extraction) */}
        {(report?.unreadable_docs || []).length > 0 && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="report-unreadable-docs">
            <p className="text-sm font-bold text-red-800">Some documents could not be read</p>
            <ul className="mt-2 space-y-1">
              {report.unreadable_docs.map((u) => (
                <li key={u.doc_type} className="text-sm text-red-700" data-testid={`unreadable-${u.doc_type}`}>
                  {u.prompt}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Missing Required docs for the founder's capital needs */}
        {capitalPurposes.length > 0 && (
          <div className="mt-6">
            <MissingCapitalDocsPanel purposes={capitalPurposes}
                                     uploadedDocTypes={uploadedDocTypes} />
          </div>
        )}

        {/* PDF-parity sections */}
        <div className="mt-6">
          <WebReportSections data={report} uploadedDocTypes={uploadedDocTypes}
                             onSnapshotUpdate={refresh} editable/>
        </div>

        {/* Action checklist (kept here — interactive) */}
        {Array.isArray(report.checklist) && report.checklist.length > 0 && (
          <ChecklistPanel report={report}
                          rerunLocked={verifiedStatus === "pending" || verifiedStatus === "verified"}
                          onRerunClick={() => nav("/onboarding?rerun=1")}
                          onBuyMoreClick={() => setBuyOpen(true)}
                          onChanged={refresh}/>
        )}

        {/* Bottom actions */}
        <div className="mt-8 flex flex-wrap items-center justify-end gap-2">
          <GetHelpLink issueType="report_download" testid="detailed-report-get-help" className="mr-auto" />
          <button onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#00828F] hover:text-[#00828F]">
            <Share2 className="h-4 w-4"/> Share report
          </button>
          <button onClick={downloadPdf}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#00828F] hover:text-[#00828F]">
            <Download className="h-4 w-4"/> Download PDF
          </button>
        </div>

        {/* Foot-of-page navigation back to the onboarding hub — founders
            scrolling to the very end of the report shouldn't have to scroll
            all the way up to find their way back. */}
        <div className="mt-10 mb-24 flex justify-center border-t border-neutral-200 pt-6">
          <Link
            to="/onboarding"
            data-testid="report-bottom-back-to-onboarding"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-[#00828F]"
          >
            <ArrowLeft className="h-4 w-4"/> Back to onboarding
          </Link>
        </div>
      </div>

      {shareOpen && (
        <ShareReportModal score={displayScore} onClose={() => setShareOpen(false)}/>
      )}
      <ShareScoreModal
        open={shareScoreOpen}
        onClose={() => setShareScoreOpen(false)}
        score={displayScore}
        companyName={report?.company?.company_name || "My company"}
        anchor="detailed"
      />
      {buyOpen && (
        <BuyCreditsModal tier={report.tier} onClose={() => setBuyOpen(false)}/>
      )}
      {celebrationModal}
    </AppShell>
  );
}


/* Interactive action checklist (kept in-page so it can mutate). */
function ChecklistPanel({ report, rerunLocked, onRerunClick, onBuyMoreClick, onChanged }) {
  const checklist = report.checklist || [];
  const doneCount = checklist.filter((i) => i.done).length;
  const totalCount = checklist.length || 1;
  const noCredits = (report.credits_balance || 0) === 0;
  const grouped = checklist.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it); return acc;
  }, {});

  const toggle = async (item) => {
    try {
      await api.post(`/founder/readiness/checklist/${item.id}/toggle`,
                     { done: !item.done });
      onChanged();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[CCR] toggle failed:", e?.message);
    }
  };

  return (
    <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
             data-testid="detailed-report-checklist">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
            Tick these off
          </p>
          <h2 className="mt-1 text-2xl font-extrabold text-neutral-900">Your action checklist</h2>
          <p className="mt-1 text-xs text-neutral-500">
            {doneCount} of {totalCount} complete · {Math.round((doneCount / totalCount) * 100)}%
          </p>
          <p className="mt-1 text-xs text-neutral-600 max-w-xl">
            Completing these {totalCount} recommendations directly increases your Capital Readiness Score.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900">
            <Coins className="h-3.5 w-3.5"/>
            {report.credits_balance} re-run credit{report.credits_balance === 1 ? "" : "s"}
          </span>
          {rerunLocked ? (
            <span data-testid="readiness-rerun-locked"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-200 px-3 py-1.5 text-xs font-bold text-neutral-500">
              <RefreshCw className="h-3.5 w-3.5"/> Re-run locked
            </span>
          ) : noCredits ? (
            <button data-testid="readiness-buy-more" onClick={onBuyMoreClick}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#D1A330] px-3 py-1.5 text-xs font-bold text-neutral-950 hover:bg-[#8A6A1E]">
              <Sparkles className="h-3.5 w-3.5"/> Add more credits
            </button>
          ) : (
            <button data-testid="readiness-rerun-btn" onClick={onRerunClick}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#004B62] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#00303F]">
              <RefreshCw className="h-3.5 w-3.5"/> Re-run my analysis (1 credit)
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full bg-[#004B62] transition-all"
             style={{ width: `${(doneCount / totalCount) * 100}%` }}/>
      </div>

      <div className="mt-5 space-y-5">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              {category}
            </p>
            <ul className="mt-2 space-y-2">
              {items.map((it) => (
                <li key={it.id} data-testid={`checklist-item-${it.id}`}
                    className="flex items-start gap-3 rounded-lg border border-neutral-100 p-3 hover:bg-neutral-50">
                  <button onClick={() => toggle(it)}
                          data-testid={`checklist-toggle-${it.id}`}
                          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                            it.done ? "border-[#00828F] bg-[#004B62]" : "border-neutral-300 bg-white hover:border-[#00828F]"
                          }`}>
                    {it.done && <CheckCircle2 className="h-3.5 w-3.5 text-white"/>}
                  </button>
                  <div className={`flex-1 ${it.done ? "opacity-60" : ""}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`text-sm font-semibold text-neutral-900 ${it.done ? "line-through" : ""}`}>
                        {it.title}
                      </p>
                      {it.auto_ticked && (
                        <span
                          data-testid={`checklist-auto-${it.id}`}
                          title="We detected this in your uploaded documents"
                          className="inline-flex items-center gap-1 rounded-full bg-[#00828F]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#00828F]"
                        >
                          Auto-detected
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-neutral-600">{it.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
